// garuda - Utility Functions

// Copy text to clipboard with fallback for non-HTTPS environments
export const copyToClipboard = async (text) => {
  // Try modern clipboard API first (requires HTTPS or localhost)
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.warn("Clipboard API failed, using fallback:", err);
      return fallbackCopyText(text);
    }
  } else {
    // Fallback for non-HTTPS or older browsers
    return fallbackCopyText(text);
  }
};

// Fallback copy method using document.execCommand
const fallbackCopyText = (text) => {
  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.style.position = "fixed";
  textArea.style.left = "-999999px";
  textArea.style.top = "-999999px";
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    const successful = document.execCommand("copy");
    document.body.removeChild(textArea);
    return successful;
  } catch (err) {
    console.error("Fallback: Unable to copy", err);
    document.body.removeChild(textArea);
    return false;
  }
};

// Play pencil scratch sound effect
export const playPencilScratch = () => {
  try {
    const audio = new Audio("/sounds/pencil_scratch.mp3");
    audio.volume = 0.3;
    audio.play().catch((err) => {
      console.warn("Audio playback failed:", err);
    });
  } catch (error) {
    console.warn("Audio initialization failed:", error);
  }
};

// Debounce function for auto-save
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Generate random hand-drawn oval path using width and height
export const generateHandDrawnCircle = (centerX, centerY, radiusX, radiusY) => {
  const points = [];
  const segments = 36;
  const wobbleX = radiusX * 0.1; // 10% wobble for imperfection
  const wobbleY = radiusY * 0.1;

  for (let i = 0; i <= segments; i++) {
    const angle = (i / segments) * Math.PI * 2;
    const randomWobbleX = (Math.random() - 0.5) * wobbleX;
    const randomWobbleY = (Math.random() - 0.5) * wobbleY;
    const rx = radiusX + randomWobbleX;
    const ry = radiusY + randomWobbleY;
    const x = centerX + rx * Math.cos(angle);
    const y = centerY + ry * Math.sin(angle);
    points.push([x, y]);
  }

  // Create SVG path from points
  let path = `M ${points[0][0]},${points[0][1]}`;

  for (let i = 1; i < points.length; i++) {
    // Use quadratic curves for smoother hand-drawn look
    if (i < points.length - 1) {
      const midX = (points[i][0] + points[i + 1][0]) / 2;
      const midY = (points[i][1] + points[i + 1][1]) / 2;
      path += ` Q ${points[i][0]},${points[i][1]} ${midX},${midY}`;
    } else {
      path += ` Q ${points[i][0]},${points[i][1]} ${points[0][0]},${points[0][1]}`;
    }
  }

  return path;
};

// Get bounding box for text element to draw oval around it
export const getTextBoundingBox = (element) => {
  if (!element) return null;
  const rect = element.getBoundingClientRect();
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
    width: rect.width,
    height: rect.height,
    radiusX: rect.width / 2 + 20, // 20px padding
    radiusY: rect.height / 2 + 15, // 15px padding for height
  };
};

// Format timestamp for display
export const formatTimestamp = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

// Format date for calendar heatmap
export const formatHeatmapDate = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

// Calculate path length for SVG animation
export const getPathLength = (pathElement) => {
  if (!pathElement) return 1000;
  return pathElement.getTotalLength();
};

// Storage helpers
export const storage = {
  get: (key, defaultValue = null) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn("Failed to parse localStorage item:", key);
      return defaultValue;
    }
  },

  set: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.warn("Failed to save to localStorage:", key);
    }
  },

  remove: (key) => {
    localStorage.removeItem(key);
  },

  clear: () => {
    localStorage.clear();
  },
};

// Mock data generators for development
export const generateMockData = {
  pencils: (count = 10) => {
    return Array.from({ length: count }, (_, i) => ({
      id: i + 1,
      title: `Fragment ${i + 1}`,
      content: `This is a cryptic thought from the void...`,
      timestamp: new Date(Date.now() - i * 86400000).toISOString(),
      tags: ["thought", "void"],
    }));
  },

  inks: (count = 5) => {
    return Array.from({ length: count }, (_, i) => ({
      id: i + 1,
      title: `Deep Dive ${i + 1}`,
      content: `# Deep Dive ${i + 1}\n\nThis is a technical writeup with math: $L = \\log(P)$\n\n## Section\n\nMore content here...`,
      timestamp: new Date(Date.now() - i * 86400000 * 7).toISOString(),
      tags: ["technical", "writeup"],
    }));
  },

  notes: () => {
    return [
      {
        id: 1,
        topic: "Philosophy",
        notes: [
          {
            id: 1,
            title: "On Existence",
            content: "# On Existence\n\nThoughts about being...",
          },
          {
            id: 2,
            title: "The Absurd",
            content: "# The Absurd\n\nCamus was right...",
          },
        ],
      },
      {
        id: 2,
        topic: "Linux",
        notes: [
          {
            id: 3,
            title: "Arch Setup",
            content:
              '# FODS Notes\n $$\n\\sigma_x=\\frac{1}{2}\n$$\n- [ ] not done\n- [x] done\n---\n- **step 1** : $\\pi d_i^2$ is circumference.\n- *step 2*\n```python\nprint("hello world!")\nquit()\n```',
          },
        ],
      },
      {
        id: 3,
        topic: "Code",
        notes: [
          {
            id: 4,
            title: "Clean Code",
            content: "# Clean Code\n\nPrinciples of good code...",
          },
        ],
      },
    ];
  },

  activities: () => {
    return [
      { id: 1, name: "Gym", color: "#666666" },
      { id: 2, name: "Coding", color: "#777777" },
      { id: 3, name: "Reading", color: "#555555" },
      { id: 4, name: "Writing", color: "#888888" },
    ];
  },

  heatmapData: (activityId, year = new Date().getFullYear()) => {
    const data = [];
    const startDate = new Date(year, 0, 1);
    const endDate = new Date(year, 11, 31);

    for (
      let d = new Date(startDate);
      d <= endDate;
      d.setDate(d.getDate() + 1)
    ) {
      if (Math.random() > 0.5) {
        data.push({
          date: formatHeatmapDate(d),
          count: 1,
        });
      }
    }

    return data;
  },

  spectacles: (count = 8) => {
    const types = ["Book", "Film", "Album", "Article"];
    return Array.from({ length: count }, (_, i) => ({
      id: i + 1,
      title: `Media ${i + 1}`,
      type: types[i % types.length],
      resonance: Math.floor(Math.random() * 100),
      review: `This is a review of media item ${i + 1}...`,
      timestamp: new Date(Date.now() - i * 86400000 * 14).toISOString(),
    }));
  },

  vault: () => {
    return [
      {
        id: 1,
        category: "Images",
        files: [
          { id: 1, name: "sketch_01.jpg", url: "#", size: "2.4 MB" },
          { id: 2, name: "photo_02.png", url: "#", size: "1.8 MB" },
        ],
      },
      {
        id: 2,
        category: "PDFs",
        files: [{ id: 3, name: "document.pdf", url: "#", size: "5.2 MB" }],
      },
      {
        id: 3,
        category: "Archives",
        files: [{ id: 4, name: "project.zip", url: "#", size: "12.4 MB" }],
      },
    ];
  },
};

// Jackpot slot machine animation for text
export const triggerJackpotAnimation = (element) => {
  if (!element || element.dataset.spinning === "true") return;

  const originalWord = element.dataset.original || element.textContent;
  element.dataset.spinning = "true";

  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%&*";
  const wordLength = originalWord.length;
  const iterations = 20;
  const intervalTime = 50;

  let currentIteration = 0;

  const interval = setInterval(() => {
    if (currentIteration >= iterations) {
      clearInterval(interval);
      element.textContent = originalWord;
      element.dataset.spinning = "false";
      return;
    }

    let newText = "";
    for (let i = 0; i < wordLength; i++) {
      const char = originalWord[i];

      // Only animate alphanumeric characters
      if (!/[a-zA-Z0-9]/.test(char)) {
        newText += char;
        continue;
      }

      // Calculate progress for each letter (stagger the animation across all letters)
      const letterDelay = Math.floor((i / wordLength) * iterations * 0.3);
      const letterProgress = currentIteration - letterDelay;
      const resolveThreshold =
        iterations - Math.floor(((wordLength - i) / wordLength) * 5) - 3;

      if (currentIteration >= resolveThreshold) {
        newText += char;
      } else if (letterProgress >= 0) {
        newText += chars[Math.floor(Math.random() * chars.length)];
      } else {
        newText += char;
      }
    }

    element.textContent = newText;
    currentIteration++;
  }, intervalTime);
};
